/**
 * Predict HOT2000 "Overall Window Characteristics" (Type 2 user-defined code) issues.
 *
 * When the entered overall U/RSI is inconsistent with modelled centre-of-glass U at a
 * given sash size, HOT2000 backs out a combined frame+edge U-value that can
 * go negative on larger windows.
 */

import { UCG_VERTICAL, EDGE_WIDTH_MM } from "./standardWindowTables";

// Table 37 — udef WindowStyle option value → area method (§13.1.2.2)
const STYLE_TO_AREA_METHOD = {
    1: 1, // Awning
    2: 1, // Casement
    3: 1, // Picture
    4: 2, // Single hung
    5: 2, // Double hung
    6: 3, // Single slider
    7: 3, // Double slider
    8: 4, // Triple slider
    9: 1, // Hopper
};

// udef FillGas H2K option value → UCG table fill key
const FILL_GAS_TO_UCG_FILL = {
    1: "1", // Air — 9 mm air table (conservative vs 13/6 mm)
    2: "4", // Argon — 9 mm argon
    3: "6", // Krypton — 9 mm krypton
};

// udef LowECoating H2K option value → UCG table coating key
const LOW_E_TO_UCG_COATING = {
    1: "0", // Clear
    2: "1", // Soft coat → Low-E .04
    3: "3", // Hard coat → Low-E .20 (hard1)
    4: "5", // Tint
    5: "6", // Tint + soft
    6: "8", // Tint + hard
};

const getOverallWindowLayer = (code = {}) => {
    const windowGroup = code?.layers?.window;
    if (!windowGroup || typeof windowGroup !== "object") {
        return null;
    }

    return Object.values(windowGroup)[0] || null;
};

const hasLegacyWindowLayer = (code = {}) => {
    const legacyGroup = code?.layers?.windowLegacy;
    return legacyGroup != null && typeof legacyGroup === "object" && Object.keys(legacyGroup).length > 0;
};

const optionValue = (field = {}) => {
    if (field.value != null && field.value !== "") {
        return `${field.value}`;
    }
    if (field.id != null && field.id !== "") {
        return `${Number(field.id) + 1}`;
    }
    return null;
};

const lookupMaxUcg = (glazingKey, coatingKey) => {
    let maxUcg = null;

    Object.keys(UCG_VERTICAL).forEach((fillKey) => {
        const ucg = UCG_VERTICAL[fillKey]?.[coatingKey]?.[glazingKey];
        if (ucg != null && (maxUcg == null || ucg > maxUcg)) {
            maxUcg = ucg;
        }
    });

    return maxUcg;
};

const lookupUcg = (layer = {}) => {
    const glazingKey = optionValue(layer.glazingType);
    const coatingKey = LOW_E_TO_UCG_COATING[optionValue(layer.lowECoating)] || "0";
    const fillKey = FILL_GAS_TO_UCG_FILL[optionValue(layer.fillGas)];

    if (!glazingKey) {
        return null;
    }

    if (fillKey) {
        return UCG_VERTICAL[fillKey]?.[coatingKey]?.[glazingKey] ?? null;
    }

    return lookupMaxUcg(glazingKey, coatingKey);
};

const getOverallU = (layer = {}, code = {}) => {
    const resistance = layer.overallThermalResistance || {};
    const rawValue = Number(resistance.value);
    const modeId = resistance.id;

    if (Number.isFinite(rawValue) && rawValue > 0) {
        // Client / import: id 0 = RSI, id 1 = U-value
        if (modeId === 1 || modeId === "1") {
            return rawValue;
        }
        return 1 / rawValue;
    }

    const nominalR = Number(code.nominalRValue);
    if (Number.isFinite(nominalR) && nominalR > 0) {
        return 1 / nominalR;
    }

    return null;
};

/**
 * Centre-of-glass area (mm²) for Overall Window Characteristics area methods.
 * Edge band = 63.5 mm.
 */
const getCentreGlassAreaMm2 = (widthMm, heightMm, frameHeightMm, areaMethod = 1) => {
    const edge = EDGE_WIDTH_MM;
    const hfr = frameHeightMm;
    let glassWidth = 0;
    let glassHeight = 0;

    switch (areaMethod) {
        case 2: // Hung
            glassWidth = widthMm - 2 * edge - 2 * hfr;
            glassHeight = heightMm - 4 * edge - 3 * hfr;
            break;
        case 3: // Slider
            glassWidth = widthMm - 4 * edge - 3 * hfr;
            glassHeight = heightMm - 2 * edge - 2 * hfr;
            break;
        case 4: // Triple slider
            glassWidth = widthMm - 2 * edge - 2 * hfr;
            glassHeight = heightMm - 6 * edge - 4 * hfr;
            break;
        case 1:
        default:
            glassWidth = widthMm - 2 * edge - 2 * hfr;
            glassHeight = heightMm - 2 * edge - 2 * hfr;
            break;
    }

    if (glassWidth <= 0 || glassHeight <= 0) {
        return {
            valid: false,
            glassWidth,
            glassHeight,
            centreGlassAreaMm2: 0,
            combinedFrameEdgeAreaMm2: 0,
            totalAreaMm2: widthMm * heightMm,
        };
    }

    const totalAreaMm2 = widthMm * heightMm;
    const centreGlassAreaMm2 = glassWidth * glassHeight;

    return {
        valid: true,
        glassWidth,
        glassHeight,
        centreGlassAreaMm2,
        combinedFrameEdgeAreaMm2: totalAreaMm2 - centreGlassAreaMm2,
        totalAreaMm2,
    };
};

const minDimensionMm = (frameHeightMm, areaMethod = 1) => {
    const edge = EDGE_WIDTH_MM;
    const hfr = frameHeightMm;

    switch (areaMethod) {
        case 2:
            return {
                widthMm: 2 * edge + 2 * hfr + 1,
                heightMm: 4 * edge + 3 * hfr + 1,
            };
        case 3:
            return {
                widthMm: 4 * edge + 3 * hfr + 1,
                heightMm: 2 * edge + 2 * hfr + 1,
            };
        case 4:
            return {
                widthMm: 2 * edge + 2 * hfr + 1,
                heightMm: 6 * edge + 4 * hfr + 1,
            };
        default:
            return {
                widthMm: 2 * edge + 2 * hfr + 1,
                heightMm: 2 * edge + 2 * hfr + 1,
            };
    }
};

/**
 * @param {object} params
 * @param {object} params.code User-defined window code object from the model code library
 * @param {number} params.widthMm Sash width in mm
 * @param {number} params.heightMm Sash height in mm
 * @returns {{ ok: boolean, issues: Array<object>, details: object }}
 */
const predictUserDefinedWindowIssues = ({ code = {}, widthMm = 0, heightMm = 0 } = {}) => {
    const issues = [];

    if (hasLegacyWindowLayer(code) && !getOverallWindowLayer(code)) {
        return { ok: true, issues, details: { skipped: "legacyUserDefinedWindow" } };
    }

    const layer = getOverallWindowLayer(code);
    if (!layer) {
        return { ok: true, issues, details: { skipped: "notOverallWindowCharacteristics" } };
    }

    const frameHeightMm = Number(layer.frameHeight);
    const areaMethod = STYLE_TO_AREA_METHOD[optionValue(layer.windowStyle)] || 1;
    const uOverall = getOverallU(layer, code);
    const uCentreGlass = lookupUcg(layer);

    const details = {
        widthMm,
        heightMm,
        frameHeightMm,
        areaMethod,
        uOverall,
        uCentreGlass,
    };

    if (!Number.isFinite(widthMm) || !Number.isFinite(heightMm) || widthMm <= 0 || heightMm <= 0) {
        return { ok: true, issues, details: { ...details, skipped: "invalidWindowDimensions" } };
    }

    if (!Number.isFinite(frameHeightMm) || frameHeightMm < 0) {
        issues.push({
            type: "missingFrameHeight",
            severity: "warning",
            message: "Overall window code is missing a frame height, so HOT2000 cannot evaluate this window reliably.",
        });
        return { ok: false, issues, details };
    }

    if (uOverall == null) {
        issues.push({
            type: "missingOverallU",
            severity: "critical",
            message: "Overall window code is missing an RSI/U-value, which HOT2000 requires for Type 2 window codes.",
        });
        return { ok: false, issues, details };
    }

    if (uCentreGlass == null) {
        issues.push({
            type: "missingCentreGlassU",
            severity: "warning",
            message: "Could not map this overall window code to a centre-of-glass U table entry for validation.",
        });
        return { ok: false, issues, details };
    }

    const areas = getCentreGlassAreaMm2(widthMm, heightMm, frameHeightMm, areaMethod);
    details.centreGlassAreaMm2 = areas.centreGlassAreaMm2;
    details.combinedFrameEdgeAreaMm2 = areas.combinedFrameEdgeAreaMm2;
    details.fCentreGlass = areas.totalAreaMm2 > 0 ? areas.centreGlassAreaMm2 / areas.totalAreaMm2 : 0;

    if (!areas.valid || areas.combinedFrameEdgeAreaMm2 <= 0) {
        const mins = minDimensionMm(frameHeightMm, areaMethod);
        issues.push({
            type: "invalidGeometry",
            severity: "critical",
            message:
                `Window size ${widthMm}×${heightMm} mm is too small for frame height ${frameHeightMm} mm ` +
                `(centre-of-glass area collapses). Minimum approx. ${mins.widthMm}×${mins.heightMm} mm for this style.`,
            details: { ...areas, minWidthMm: mins.widthMm, minHeightMm: mins.heightMm },
        });
        return { ok: false, issues, details };
    }

    const uCombinedFrameEdge =
        (uOverall * areas.totalAreaMm2 - uCentreGlass * areas.centreGlassAreaMm2) / areas.combinedFrameEdgeAreaMm2;

    details.uCombinedFrameEdge = uCombinedFrameEdge;

    if (uCombinedFrameEdge < 0) {
        issues.push({
            type: "negativeCombinedFrameEdgeU",
            severity: "critical",
            message:
                `HOT2000 will calculate a negative combined frame-edge U-value for this window ` +
                `(${widthMm}×${heightMm} mm) with code "${code.label || code.codeRef || ""}". ` +
                `The entered overall U (≈${uOverall.toFixed(2)} W/m²K) is too low relative to the modelled ` +
                `centre-of-glass U (≈${uCentreGlass.toFixed(2)} W/m²K) at this size and frame height (${frameHeightMm} mm). ` +
                `Try a larger overall U (lower RSI), a smaller frame height, or switch to a different window style.`,
            details: {
                uOverall,
                uCentreGlass,
                uCombinedFrameEdge,
                fCentreGlass: details.fCentreGlass,
            },
        });
    }

    return { ok: issues.length === 0, issues, details };
};

const isOverallUserDefinedWindowCode = (codeRef = "") => {
    const parts = `${codeRef}`.split("-");
    return parts[0] === "Window" && parts[1] === "U";
};

export const getUserDefinedWindowTypeWarning = ({ codeRef = "", code = {}, widthMm = 0, heightMm = 0 } = {}) => {
    if (!isOverallUserDefinedWindowCode(codeRef)) {
        return "";
    }

    const { issues = [] } = predictUserDefinedWindowIssues({ code, widthMm, heightMm });

    if (issues.length === 0) {
        return "";
    }

    const primaryIssue = issues.find(({ severity }) => severity === "critical") || issues[0];

    return primaryIssue.message || "";
};

export { predictUserDefinedWindowIssues, getOverallWindowLayer, getCentreGlassAreaMm2, isOverallUserDefinedWindowCode };
